import { cva, type VariantProps } from "class-variance-authority";
import type { ReactNode } from "react";

import { cn } from "../../utils/cn.util";
import { IconSwap } from "../icon-swap";
import { LoadingIndicator } from "../loading-indicator";

// Icon size per the Figma design system. Buttons (`sm`–`2xl`) use 20px icons; the sizes outside it keep 16px (`3xs`–`xs`)
// and 20px (`input`). Icon buttons use 16px at `3xs`, 24px at `2xl`, and 20px otherwise. It targets the svg, so it goes on
// the svg's parent: the `LoadingSwap` items, the `LoadingSlot`, or an `asChild` icon button itself.
export const iconSizeVariants = cva("", {
  variants: {
    size: {
      input: "[&>svg]:size-5",
      "3xs": "[&>svg]:size-4",
      xxs: "",
      xs: "",
      sm: "[&>svg]:size-5",
      md: "[&>svg]:size-5",
      lg: "[&>svg]:size-5",
      xl: "[&>svg]:size-5",
      "2xl": "",
    },
    icon: {
      true: "",
      false: "",
    },
  },
  // Only the sizes where buttons and icon buttons differ, so no two classes compete.
  compoundVariants: [
    { icon: false, size: ["xxs", "xs"], className: "[&>svg]:size-4" },
    { icon: true, size: ["xxs", "xs"], className: "[&>svg]:size-5" },
    { icon: false, size: "2xl", className: "[&>svg]:size-5" },
    { icon: true, size: "2xl", className: "[&>svg]:size-6" },
  ],
  defaultVariants: {
    size: "sm",
    icon: false,
  },
});

type LoadingSize = VariantProps<typeof iconSizeVariants>["size"];

interface LoadingSlotProps {
  isLoading: boolean;
  size?: LoadingSize;
}

/** A spinner prepended to the label that widens the button smoothly, for buttons with no icon to swap it with. */
export const LoadingSlot = ({ isLoading, size }: LoadingSlotProps) => (
  <span
    className={cn(
      iconSizeVariants({ size }),
      // Grows in (`starting:`) and shrinks out (`transition-discrete` animates the `hidden` toggle), clipping the spinner
      // as it goes; while hidden it takes no space.
      "flex shrink-0 items-center justify-center overflow-hidden transition-[display,max-width,margin-right,opacity] transition-discrete duration-200 ease-out starting:-mr-2 starting:max-w-0 starting:opacity-0",
      // `!` so consumer display utilities (e.g. `[&>span]:line-clamp-none`) can't reveal the idle slot.
      isLoading ? "max-w-5" : "-mr-2 hidden! max-w-0 opacity-0"
    )}
  >
    <LoadingIndicator className="text-current" />
  </span>
);

interface LoadingSwapProps {
  isLoading: boolean;
  size?: LoadingSize;
  /** Sizes the icon and the spinner as an icon button's icon rather than as `iconLeading`/`iconTrailing`. */
  icon?: boolean;
  children: ReactNode;
}

/** Swaps `children` for a spinner in place while loading. */
export const LoadingSwap = ({ isLoading, size, icon = false, children }: LoadingSwapProps) => {
  const itemClassName = iconSizeVariants({ size, icon });

  return (
    <IconSwap.Root value={isLoading ? "loading" : "idle"}>
      <IconSwap.Item value="idle" className={itemClassName}>
        {children}
      </IconSwap.Item>
      <IconSwap.Item value="loading" className={itemClassName}>
        <LoadingIndicator className="text-current" />
      </IconSwap.Item>
    </IconSwap.Root>
  );
};
